import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import {
  actOnDraft,
  changesPanelQuery,
  resultOf,
  setAgentMode,
  type AgentMode,
  type ChangeAnswer,
  type Draft,
  type DraftAction,
} from '../api/changes'
import { ApiError } from '../api/client'
import { Forbidden } from '../ui/Forbidden'
import { Spinner } from '../ui/Spinner'

/**
 * Change Proposals - `views/version-3/changes/_panel.php` + `change_drawer.js`.
 *
 * Ce vrea AI-ul sa ruleze pe flota, si cine aproba. Comutatorul de mod sus,
 * apoi propunerile: comanda, lista inghetata de masini (prima e canary), si
 * butoanele de aprobare. Aprobarea trimite primul val; restul le conduce API-ul.
 *
 * ACELASI PANOU, IN DOUA LOCURI: sertarul din dreapta (calea obisnuita) si
 * pagina intreaga `/change` (adresa directa). Un al doilea panou, copiat, ar
 * fi divergat la prima schimbare - de-aia si filtrul de numere al sertarului
 * agentului (`ids`) e tot aici, nu alt ecran.
 *
 * Culorile si asezarea sunt scrise pe element, ca in macheta: panoul e desenat
 * si in sertar, si in pagina, si o foaie a paginii nu l-ar fi ajuns in sertar.
 */

const STATUS_CHIP: Record<string, [string, string, string]> = {
  proposed: ['#8a6116', '#fdf3dd', 'Waiting for approval'],
  approved: ['#1f5f8b', '#e3f0fa', 'Approved'],
  running: ['#1f5f8b', '#e3f0fa', 'Running'],
  done: ['#2e7d32', '#e6f4e6', 'Done'],
  stopped: ['#b42318', '#fdecea', 'Stopped'],
  rejected: ['#6b7480', '#eef0f2', 'Rejected'],
}

const CAB_CHIP: Record<string, [string, string, string]> = {
  none: ['#6b7480', '#eef0f2', 'No CAB ticket'],
  pending: ['#8a6116', '#fdf3dd', 'CAB pending'],
  approved: ['#2e7d32', '#e6f4e6', 'CAB approved'],
  rejected: ['#b42318', '#fdecea', 'CAB rejected'],
  standard: ['#1f5f8b', '#e3f0fa', 'Standard change'],
}

const CONSENT_CHIP: Record<string, [string, string, string]> = {
  pending: ['#8a6116', '#fdf3dd', 'Window: not answered'],
  given: ['#2e7d32', '#e6f4e6', 'Window: agreed'],
  declined: ['#b42318', '#fdecea', 'Window: declined'],
}

const DOT_COLOR: Record<string, string> = { ok: '#2e7d32', error: '#b42318', canceled: '#6b7480', waiting: '#8a6116' }

/** Cat sta un buton "armat" (Sure?) pana revine singur - `change_drawer.js`. */
const ARM_MS = 4000

const CHIP: CSSProperties = { padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 }
const PRE: CSSProperties = { margin: '8px 0 0', padding: '6px 10px', background: '#f6f7f9', borderRadius: 6, fontSize: 12, whiteSpace: 'pre-wrap' }

export interface ChangeProposalsPanelProps {
  /** Doar propunerile discutiei sertarului agentului (`?ids=`); gol = toate. */
  ids?: number[]
  /** Numai ciornele nascute din biroul AI (`?desk=1`). */
  deskOnly?: boolean
}

export function ChangeProposalsPanel({ ids = [], deskOnly = false }: ChangeProposalsPanelProps) {
  const client = useQueryClient()
  const panel = useQuery(changesPanelQuery(ids, deskOnly))

  /* Un singur drum odata: butoanele se sting cat zboara cererea - un
     dublu-click nu mai poate trimite acelasi lucru de doua ori (serverul are
     si el zavorul lui, dar ecranul nu trebuie sa-l puna la incercare). */
  const [inFlight, setInFlight] = useState(false)
  const [modeMsg, setModeMsg] = useState<{ text: string; color: string }>({ text: '', color: '#6b7480' })
  const [draftMsg, setDraftMsg] = useState<Record<number, { text: string; color: string }>>({})
  const [armed, setArmed] = useState<string | null>(null)
  const armTimer = useRef<number | null>(null)

  useEffect(() => () => {
    if (armTimer.current !== null) window.clearTimeout(armTimer.current)
  }, [])

  function arm(key: string): boolean {
    if (armed === key) {
      setArmed(null)
      return true
    }
    setArmed(key)
    if (armTimer.current !== null) window.clearTimeout(armTimer.current)
    armTimer.current = window.setTimeout(() => setArmed(null), ARM_MS)
    return false
  }

  function refresh() {
    void client.invalidateQueries({ queryKey: ['changes'] })
  }

  async function post(run: () => Promise<ChangeAnswer>, say: (m: { text: string; color: string }) => void) {
    if (inFlight) return
    setInFlight(true)
    say({ text: 'Working...', color: '#6b7480' })
    try {
      const r = await run()
      if (r && r.ok === false) {
        say({ text: r.error || 'Failed.', color: '#b42318' })
        return
      }
      say({ text: '', color: '#6b7480' })
      refresh()
    } catch (e) {
      say({ text: e instanceof ApiError ? e.message : 'The request failed.', color: '#b42318' })
    } finally {
      setInFlight(false)
    }
  }

  function pickMode(mode: AgentMode) {
    /* Incident se armeaza la a doua apasare. */
    if (mode === 'incident' && !arm('mode:incident')) return
    void post(() => setAgentMode(mode), setModeMsg)
  }

  function act(draft: number, action: DraftAction) {
    if ((action === 'approve' || action === 'stop') && !arm(`${action}:${draft}`)) return
    void post(
      () => actOnDraft(draft, action),
      (m) => setDraftMsg((all) => ({ ...all, [draft]: m })),
    )
  }

  if (panel.error instanceof ApiError && panel.error.status === 403) return <Forbidden />
  if (panel.error) {
    return (
      <div className="od-change-loading" style={{ color: '#b42318' }}>
        Could not load. Try Refresh.
      </div>
    )
  }
  if (!panel.data) return <Spinner />

  const data = panel.data
  const curMode = data.agentMode.mode in data.modes ? data.agentMode.mode : 'problem'
  const curMeta = data.modes[curMode as keyof typeof data.modes]

  return (
    <div className="row od-change-panel">
      <div className="col-md-12">
        {data.blockers.length > 0 && (
          <div className="panel panel-default" style={{ borderLeft: '4px solid #b42318' }}>
            <div className="panel-body" style={{ background: '#fdecea' }}>
              <strong style={{ color: '#b42318' }}>Approve will not work yet:</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 13 }}>
                {data.blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
              <div style={{ marginTop: 6, fontSize: 12 }}>
                You can apply them in one click under <a href="/app/settings/change_log">Change Log &rarr; Database migrations</a>.
              </div>
            </div>
          </div>
        )}

        {/* Comutatorul de mod. */}
        <div className="panel panel-default">
          <div className="panel-body" style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13 }}>Agent mode</strong>
            <div style={{ display: 'inline-flex', border: '1px solid #d5dae1', borderRadius: 7, overflow: 'hidden' }}>
              {(Object.keys(data.modes) as Array<keyof typeof data.modes>).map((key) => {
                const meta = data.modes[key]
                const isCur = curMode === key
                const isArmed = key === 'incident' && armed === 'mode:incident'
                return (
                  <button
                    key={key}
                    type="button"
                    className="od-cm-pick"
                    data-mode={key}
                    disabled={inFlight}
                    onClick={() => pickMode(key)}
                    style={{
                      border: 0,
                      padding: '7px 16px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      background: isCur ? meta.color : '#fff',
                      color: isCur ? '#fff' : '#3d4752',
                    }}
                  >
                    {isArmed ? 'Sure?' : meta.label}
                  </button>
                )
              })}
            </div>
            <span className="text-muted" style={{ fontSize: 12 }}>
              {curMeta.hint}
            </span>
            <span className="od-cm-msg" style={{ fontSize: 12, color: modeMsg.color }}>
              {modeMsg.text}
            </span>
          </div>
        </div>

        {data.drafts.length === 0 && (
          <div className="panel panel-default">
            <div className="panel-body text-muted">
              {/* Doua goluri diferite, si nu e acelasi lucru: "discutia n-a
                  propus nimic" nu inseamna ca firma n-are propuneri. */}
              {data.onlySession
                ? 'Nothing proposed in this conversation yet.'
                : 'Nothing proposed yet. Proposals come from the AI agent and wait here for a signature.'}
            </div>
          </div>
        )}

        {data.drafts.map((draft) => (
          <DraftCard
            key={draft.ID}
            draft={draft}
            inFlight={inFlight}
            armed={armed}
            message={draftMsg[draft.ID]}
            onAct={(action) => act(draft.ID, action)}
          />
        ))}
      </div>
    </div>
  )
}

function DraftCard({
  draft,
  inFlight,
  armed,
  message,
  onAct,
}: {
  draft: Draft
  inFlight: boolean
  armed: string | null
  message?: { text: string; color: string }
  onAct: (action: DraftAction) => void
}) {
  const chip = STATUS_CHIP[draft.status] ?? STATUS_CHIP.rejected
  const total = draft.device_list.length
  const live = draft.status === 'approved' || draft.status === 'running'
  const cabChip = CAB_CHIP[draft.cab_state ?? 'none'] ?? CAB_CHIP.none
  const consentChip = draft.consent_state ? CONSENT_CHIP[draft.consent_state] : null
  const selfApproval = draft.approve_block === 'self_approval'
  const consentDeclined = draft.consent_state === 'declined'
  const cabBlocksApprove = draft.cab_state === 'pending' || draft.cab_state === 'rejected'
  const approveTitle = selfApproval
    ? SELF_APPROVAL_REASON
    : consentDeclined
      ? 'The requester declined the proposed window.'
      : draft.cab_state === 'pending'
        ? 'Wait until the CAB decides on the linked change ticket.'
        : draft.cab_state === 'rejected'
          ? 'The CAB rejected the linked change ticket.'
          : ''

  return (
    <div className="panel panel-default">
      <div className="panel-body">
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 14 }}>{draft.title}</strong>
              {draft.mode === 'incident' && (
                <span style={{ ...CHIP, padding: '2px 9px', background: '#b42318', color: '#fff' }}>INCIDENT</span>
              )}
              {/* Cine a cerut-o. Fara semnul asta, orice rand de aici pare scris de
                  agent - si cine aproba judeca altfel o comanda scrisa de un om. */}
              {draft.proposed_by === 'plan' && (
                <span style={{ ...CHIP, padding: '2px 9px', background: '#1f6f8b', color: '#fff' }} title="Launched from a plan step by a technician">
                  PLAN
                </span>
              )}
              {draft.proposed_by !== 'ai' && draft.proposed_by !== 'plan' && (
                <span
                  style={{ ...CHIP, padding: '2px 9px', background: '#eef2f7', color: '#33475b' }}
                  title="Asked for from the portal, by a person - not proposed by the agent"
                >
                  BY HAND
                </span>
              )}
              <span style={{ ...CHIP, background: chip[1], color: chip[0] }}>{chip[2]}</span>
              {(draft.change_ticket_id ?? 0) > 0 && (
                <span style={{ ...CHIP, background: cabChip[1], color: cabChip[0] }} title="CAB change ticket">
                  {cabChip[2]}
                </span>
              )}
              {consentChip && (
                <span style={{ ...CHIP, background: consentChip[1], color: consentChip[0] }} title="Requester window consent">
                  {consentChip[2]}
                </span>
              )}
              <span className="text-muted" style={{ fontSize: 12 }}>
                #{draft.ID} &middot; {draft.command_type} &middot; {total} machines &middot; waves {draft.waves} &middot; stops
                at {draft.stop_after_failures} failure(s)
              </span>
            </div>

            {(draft.ticket_id ?? 0) > 0 && draft.proposed_by === 'ai' && (
              <div className="text-muted" style={{ marginTop: 6, fontSize: 12 }}>
                From desk ticket{' '}
                <a href={`/app/easeedesk/ticket/${draft.ticket_id}`} target="_blank" rel="noreferrer">
                  #{draft.ticket_id}
                </a>
                {draft.requested_by_email ? ` — asked by ${draft.requested_by_email}` : ''}
              </div>
            )}
            {(draft.change_ticket_id ?? 0) > 0 && (
              <div className="text-muted" style={{ marginTop: 4, fontSize: 12 }}>
                Change ticket{' '}
                <a href={`/app/easeedesk/ticket/${draft.change_ticket_id}`} target="_blank" rel="noreferrer">
                  #{draft.change_ticket_id}
                </a>
              </div>
            )}
            {(draft.window_start ?? '') !== '' && (draft.window_end ?? '') !== '' && (
              <div className="text-muted" style={{ marginTop: 4, fontSize: 12 }}>
                Proposed window: {draft.window_start} – {draft.window_end}
              </div>
            )}

            {draft.intent !== '' && (
              <div className="text-muted" style={{ marginTop: 6, fontSize: 12, fontStyle: 'italic' }}>
                &ldquo;{draft.intent}&rdquo;
              </div>
            )}

            {draft.command !== '' && <pre style={PRE}>{draft.command}</pre>}

            {/* Starea din coada, pe fiecare masina: bulina spune ce a raspuns, nu doar cate. */}
            <div style={{ marginTop: 8, fontSize: 12 }}>
              {draft.device_list.map((device, i) => {
                const isCanary = i === 0 && draft.mode !== 'incident'
                const res = resultOf(draft, device.id)
                const dot = res ? (DOT_COLOR[res.state] ?? '#6b7480') : null
                const tip = res ? res.state + (res.note !== '' ? ': ' + res.note : '') : ''
                return (
                  <span
                    key={device.id}
                    title={tip}
                    style={{
                      display: 'inline-block',
                      margin: '2px 6px 2px 0',
                      padding: '2px 8px',
                      border: '1px solid ' + (isCanary ? '#8a6116' : '#dfe3e8'),
                      borderRadius: 10,
                      background: isCanary ? '#fdf3dd' : undefined,
                    }}
                  >
                    {dot && (
                      <span
                        style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: dot, marginRight: 5 }}
                      ></span>
                    )}
                    {isCanary ? 'canary: ' : ''}
                    {device.name}
                  </span>
                )
              })}
            </div>

            {(draft.status === 'running' || draft.status === 'done' || draft.status === 'stopped') && (
              <div style={{ marginTop: 8, fontSize: 12 }}>
                waves sent: <strong>{draft.waves_sent}</strong> &middot; succeeded:{' '}
                <strong style={{ color: '#2e7d32' }}>{draft.succeeded}</strong> &middot; failed:{' '}
                <strong style={{ color: '#b42318' }}>{draft.failed}</strong>
              </div>
            )}

            {draft.approved_by_name !== '' && (
              <div className="text-muted" style={{ marginTop: 6, fontSize: 11 }}>
                {draft.status === 'rejected' ? 'Rejected' : 'Approved'} by {draft.approved_by_name} at {draft.approved_at}
              </div>
            )}
            {/* Biroul inchide ciorna cu approved_by 0: refuz CAB, refuzul solicitantului sau timeout. */}
            {draft.approved_by_name === '' && draft.status === 'rejected' && draft.proposed_by === 'ai' && (draft.ticket_id ?? 0) > 0 && (
              <div className="text-muted od-desk-rejected" style={{ marginTop: 6, fontSize: 11 }}>
                Rejected by the desk: {deskRejectReason(draft)}
              </div>
            )}
          </div>

          <div style={{ flex: '0 0 auto', textAlign: 'right' }}>
            {draft.status === 'proposed' && (
              <>
                {draft.cab_state !== 'rejected' && (
                  <button
                    type="button"
                    className="btn btn-success btn-sm od-approve"
                    disabled={inFlight || cabBlocksApprove || selfApproval || consentDeclined}
                    title={approveTitle}
                    onClick={() => onAct('approve')}
                  >
                    {armed === `approve:${draft.ID}` ? 'Sure? send now' : 'Approve & start'}
                  </button>
                )}{' '}
                <button type="button" className="btn btn-default btn-sm od-reject" disabled={inFlight} onClick={() => onAct('reject')}>
                  Reject
                </button>
                {selfApproval && (
                  <div className="text-muted od-self-approval" style={{ fontSize: 11, marginTop: 6, maxWidth: 240 }}>
                    {SELF_APPROVAL_REASON}
                  </div>
                )}
              </>
            )}
            {live && (
              <>
                <button type="button" className="btn btn-danger btn-sm od-stop" disabled={inFlight} onClick={() => onAct('stop')}>
                  {armed === `stop:${draft.ID}` ? 'Sure? stop' : 'Stop now'}
                </button>{' '}
                <button
                  type="button"
                  className="btn btn-default btn-sm od-done"
                  title="Checked on the machine - close it"
                  disabled={inFlight}
                  onClick={() => onAct('done')}
                >
                  Mark done
                </button>
              </>
            )}
            {draft.status === 'proposed' && (draft.window_start ?? '') !== '' && (
              <div className="text-muted od-window-hint" style={{ fontSize: 11, marginTop: 6, maxWidth: 240 }}>
                Window starts at {draft.window_start} - approve when wave 1 should go
              </div>
            )}
            <div className="od-msg" style={{ fontSize: 12, marginTop: 6, maxWidth: 240, textAlign: 'right', color: message?.color }}>
              {message?.text ?? ''}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const SELF_APPROVAL_REASON = 'You asked for this change (or it is about your own account) - another approver has to decide.'

function deskRejectReason(draft: Draft): string {
  if (draft.cab_state === 'rejected') return 'the CAB rejected the change ticket'
  if (draft.consent_state === 'declined') return 'the requester declined the window'
  return 'nobody approved it in time'
}
